/**
 * ProductBasicContent - 产品基础信息渲染组件
 */

import React from 'react';

export default function ProductBasicContent({ data, mode = 'view', templateType }) {
  if (!data) return null;

  // 标准模板需要的字段（默认顺序）
  const standardFields = [
    { key: 'productName', label: '产品名称' },
    { key: 'ingredients', label: '配料表' },
    { key: 'netContent', label: '净含量/规格' },
    { key: 'standardCode', label: '产品标准代号' }
  ];

  // 其他字段（默认顺序）
  const otherFields = [
    { key: 'manufacturer', label: '生产商' },
    { key: 'address', label: '生产地址' },
    { key: 'origin', label: '产地' },
    { key: 'productionDate', label: '生产日期' },
    { key: 'shelfLife', label: '保质期' },
    { key: 'storageConditions', label: '储存条件' },
    { key: 'productionLicense', label: '生产许可证' },
    { key: 'serviceHotline', label: '服务热线' }
  ];

  const isStandardTemplate = templateType === 'standard-food-label';
  const isBlueWhiteTemplate = templateType === 'blue-white-template';
  const ts = data.tableStyle || {};
  const baseFields = [...standardFields, ...otherFields];
  const baseFieldMap = baseFields.reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {});
  const normalizedCustomFields = (data.customFields || []).map((field, idx) => ({
    ...field,
    _id: field._id || `custom-${idx}`
  }));
  const customFieldMap = normalizedCustomFields.reduce((acc, field) => {
    acc[field._id] = field;
    return acc;
  }, {});
  const defaultOrder = [
    ...baseFields.map(field => field.key),
    ...normalizedCustomFields.map(field => field._id)
  ];
  const order = Array.isArray(data.fieldOrder) && data.fieldOrder.length > 0
    ? data.fieldOrder
    : defaultOrder;
  const orderedFields = order
    .map((id) => {
      const baseField = baseFieldMap[id];
      if (baseField) {
        return { ...baseField, value: data[baseField.key] || '' };
      }
      const customField = customFieldMap[id];
      if (customField) {
        return { key: id, label: customField.label, value: customField.value || '', type: 'custom' };
      }
      return null;
    })
    .filter(Boolean);
  const visibleOrderedFields = orderedFields.filter(field => field.value);

  // 获取标题配置
  const titleConfig = data.titleConfig || {};
  const shouldShowCustomTitle = titleConfig.enabled && (titleConfig.chinese || titleConfig.english);

  // 根据模板类型设置默认颜色
  const defaultBgColor = isBlueWhiteTemplate ? '#1e90ff' : (isStandardTemplate ? '#5C4A37' : '#1e90ff');

  // 渲染标题
  const renderTitle = () => {
    if (shouldShowCustomTitle) {
      if (isBlueWhiteTemplate) {
        return (
          <h3
            className="content-section-heading blue-white-title"
            style={{
              backgroundColor: titleConfig.bgColor || defaultBgColor,
              color: titleConfig.textColor || '#ffffff'
            }}
          >
            {titleConfig.english && (
              <div className="blue-white-title-en">{titleConfig.english}</div>
            )}
            <div className="blue-white-title-cn">{titleConfig.chinese || '产品基础信息'}</div>
          </h3>
        );
      }

      const resolvedBgColor = (() => {
        const base = titleConfig.bgColor || defaultBgColor;
        if (isStandardTemplate && base === '#1e90ff') {
          return '#5C4A37';
        }
        return base;
      })();
      return (
        <h3
          className="content-section-heading"
          style={{
            backgroundColor: resolvedBgColor,
            color: titleConfig.textColor || '#ffffff',
            padding: '8px 12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}
        >
          {titleConfig.english && (
            <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.2 }}>
              {titleConfig.english}
            </div>
          )}
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
            {titleConfig.chinese || '产品基础信息'}
          </div>
        </h3>
      );
    }

    if (isStandardTemplate || isBlueWhiteTemplate) {
      return <h3 className="content-section-heading">产品基础信息</h3>;
    }

    return null;
  };

  // 标准模板 / 蓝白模板 使用表格布局
  if (isStandardTemplate || isBlueWhiteTemplate) {
    const table = (
      <div className="label-content-product-basic">
        <table className="info-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {visibleOrderedFields.map(field => (
              <tr key={field.key} className="info-table-row">
                <td className="info-table-label" style={{ background: ts.labelBgColor, color: ts.labelTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{field.label}</td>
                <td className="info-table-value" style={{ color: ts.valueTextColor, borderBottom: ts.rowDividerColor ? `1px solid ${ts.rowDividerColor}` : undefined, padding: (ts.cellPadding ?? 12) + 'px' }}>{field.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    // 蓝白模板：标题放置在白卡片外，显示在蓝色背景上
    if (isBlueWhiteTemplate) {
      return (
        <>
          {renderTitle()}
          {table}
        </>
      );
    }
    // 标准模板：标题仍在卡片内部
    return (
      <div className="label-content-product-basic">
        {renderTitle()}
        {table.props.children}
      </div>
    );
  }

  // 默认网格布局
  return (
    <div className="label-content-product-basic">
      <h3 className="content-section-heading">产品信息</h3>
      <div className="info-grid">
        {visibleOrderedFields.map(field => (
          <div key={field.key} className="info-item">
            <span className="info-label">{field.label}：</span>
            <span className="info-value">{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
